import { Box, IconButton, Typography } from '@mui/material';
import { Icon, Navigation } from '@shopify/polaris';
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  ExitIcon,
  DeliveryFilledIcon,
  DeliveryIcon,
  HomeFilledIcon,
  HomeIcon,
  InventoryFilledIcon,
  InventoryIcon,
  PackageReturnedIcon,
  PackageFilledIcon,
  PrintIcon,
  PackageIcon,
  SettingsFilledIcon,
  SettingsIcon,
} from '@shopify/polaris-icons';
import ExportIcon from 'src/assets/icons/export_icon';
import OrderMenuIcon from 'src/assets/icons/order_icon';
import OrderFillMenuIcon from 'src/assets/icons/order_fill_icon';
import ReturnOrderMenuIcon from 'src/assets/icons/return_order_icon';
import ReturnOrderFillMenuIcon from 'src/assets/icons/return_order_fill_icon';
import BulkOrderMenuIcon from 'src/assets/icons/bulk_order_icon';
import BulkOrderFillMenuIcon from 'src/assets/icons/bulk_order_fill_icon';
import BulkOutboundIcon from 'src/assets/icons/bulk_outbound_icon';
import BulkOutboundFillIcon from 'src/assets/icons/bulk_outbound_fill_icon';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { usePathname } from 'src/hooks/router/use_pathname';
import { useRouter } from 'src/hooks/router/use_router';
import { PATHS } from 'src/routes/paths';
import { chooseLan } from 'src/utils/language_code';
import NavSectionVertical from 'src/components/navigation/vertical/nav_vertical';
import NavSectionMini from 'src/components/navigation/mini/nav_mini';
import { useGetRoleMenu, useGetSettingMenu } from 'src/api/menu_api';
import Loading from 'src/components/loading';
import i18n from 'src/locales/i18n';
import { APP_SHORT_NAME } from 'src/config';
import { NAV_WIDTH_CLOSED, NAV_WIDTH_OPEN } from './dashboard_layout';
import {
  SETTINGS_ICONS,
  SETTINGS_MENU_NAME,
  SETTINGS_ROUTES,
  toSettingsKey,
} from 'src/sections/settings/settings_menu';

i18n.loadNamespaces(['sorting']);

const Navbar = ({ navOpen, setNavOpen }) => {
  const { t, i18n } = useTranslation();
  const pathName = usePathname();
  const router = useRouter();
  const { searchResults, searchLoading } = useGetRoleMenu();
  const { searchResults: settingMenus, isSettingAccess } = useGetSettingMenu();

  const menuHelper = (menuName, childrenCount) => {
    menuName = menuName.toLowerCase();
    if (menuName === 'orders')
      return {
        path: PATHS.dashboard.order.order.root,
        icon: OrderFillMenuIcon,
        selectedIcon: OrderMenuIcon,
      };
    if (menuName === 'bulk orders')
      return {
        path: PATHS.dashboard.bulk_order.root,
        icon: BulkOrderFillMenuIcon,
        selectedIcon: BulkOrderMenuIcon,
      };
    if (menuName === 'b2b orders')
      return {
        path: PATHS.dashboard.bulk_order.b2b.root,
      };
    if (menuName === 'withdrawals')
      return {
        path: PATHS.dashboard.bulk_order.withdrawal.root,
      };
    if (menuName === 'transfers')
      return {
        path: PATHS.dashboard.bulk_order.transfer.root,
      };
    if (menuName === 'return orders')
      return {
        path: PATHS.dashboard.return.order.root,
        icon: ReturnOrderFillMenuIcon,
        selectedIcon: ReturnOrderMenuIcon,
      };
    if (menuName === 'return inbound')
      return {
        path: PATHS.dashboard.return_inbound.root,
        icon: PackageReturnedIcon,
        selectedIcon: PackageReturnedIcon,
      };
    if (menuName === 'scan tracking number')
      return {
        path: PATHS.dashboard.return_inbound.scan_tracking,
      };
    if (menuName === 'inbound' && childrenCount > 0)
      return {
        path: PATHS.dashboard.inbound.root,
        icon: PackageFilledIcon,
        selectedIcon: PackageIcon,
      };
    if (menuName === 'inbound' && childrenCount === 0)
      return {
        path: PATHS.dashboard.inbound.inbound.root,
      };
    if (menuName === 'bulk put away')
      return {
        path: PATHS.dashboard.inbound.bulk_putaway.root,
      };
    if (menuName === 'put away')
      return {
        path: PATHS.dashboard.inbound.putaway.root,
      };
    if (menuName === 'outbound' && childrenCount > 0)
      return {
        path: PATHS.dashboard.outbound.root,
        icon: DeliveryFilledIcon,
        selectedIcon: DeliveryIcon,
      };
    if (menuName === 'outbound' && childrenCount === 0)
      return {
        path: PATHS.dashboard.outbound.outbound.root,
        icon: DeliveryFilledIcon,
        selectedIcon: DeliveryIcon,
      };
    if (menuName === 'picking')
      return {
        path: PATHS.dashboard.outbound.picking.root,
      };
    if (menuName === 'sorting')
      return {
        path: PATHS.dashboard.outbound.sorting.root,
      };
    if (menuName === 'packing')
      return {
        path: PATHS.dashboard.outbound.packing.root,
      };
    if (menuName === 'single packing')
      return {
        path: PATHS.dashboard.outbound.single_packing.root,
      };
    if (menuName === 'shipping scan')
      return {
        path: PATHS.dashboard.outbound.shipping.scan.root,
      };
    if (menuName === 'advance tracking no.')
      return { path: PATHS.dashboard.outbound.advance_tracking_number.root };
    if (menuName === 'bulk outbound' && childrenCount > 0)
      return {
        path: PATHS.dashboard.bulk_outbound.root,
        icon: BulkOutboundFillIcon,
        selectedIcon: BulkOutboundIcon,
      };
    if (menuName === 'bulk outbound' && childrenCount === 0)
      return {
        path: PATHS.dashboard.bulk_outbound.bulk_outbound.root,
        icon: BulkOutboundFillIcon,
        selectedIcon: BulkOutboundIcon,
      };
    if (menuName === 'bulk picking')
      return {
        path: PATHS.dashboard.bulk_outbound.bulk_picking.root,
      };
    if (menuName === 'bulk packing')
      return {
        path: PATHS.dashboard.bulk_outbound.bulk_packing.root,
      };
    if (menuName === 'bulk shipping scan')
      return {
        path: PATHS.dashboard.bulk_outbound.bulk_shipping.scan.root,
      };
    if (menuName === 'inventory' && childrenCount === 0)
      return {
        path: PATHS.dashboard.inventory.stock.root,
      };
    if (menuName === 'inventory')
      return {
        path: PATHS.dashboard.inventory.root,
        icon: InventoryFilledIcon,
        selectedIcon: InventoryIcon,
      };
    if (menuName === 'products')
      return {
        path: PATHS.dashboard.inventory.products.root,
      };
    if (menuName === 'outer boxes')
      return {
        path: PATHS.dashboard.inventory.outerbox.root,
      };
    if (menuName === 'adjustment tasks')
      return {
        path: PATHS.dashboard.inventory.adjustment_tasks.root,
      };
    if (menuName === 'adjustment management')
      return {
        path: PATHS.dashboard.inventory.adjustment_management.root,
      };
    if (menuName === 'transfer')
      return {
        path: PATHS.dashboard.inventory.transfer.root,
      };
    if (menuName === 'bulk transfer')
      return {
        path: PATHS.dashboard.inventory.bulk_transfer.root,
      };
    if (menuName === 'purchase orders')
      return { path: PATHS.dashboard.inventory.purchase_order.root };
    if (menuName === 'export')
      return {
        path: PATHS.dashboard.export.root,
        icon: ExportIcon,
        selectedIcon: ExportIcon,
      };
    if (menuName === 'carton')
      return {
        path: PATHS.dashboard.export.carton.root,
      };
    if (menuName === 'manifest')
      return {
        path: PATHS.dashboard.export.manifest.root,
      };
    if (menuName === 'ready to manifest')
      return {
        path: PATHS.dashboard.export.ready_to_manifest.root,
      };

    if (menuName === 'printing' && childrenCount > 0)
      return {
        path: null,
        icon: PrintIcon,
        selectedIcon: PrintIcon,
      };
    if (menuName === 'printing')
      return {
        path: PATHS.dashboard.printing.root,
        icon: PrintIcon,
        selectedIcon: PrintIcon,
      };
    if (menuName === 'label printing') return { path: PATHS.dashboard.printing.label };
    if (menuName === 'document printing') return { path: PATHS.dashboard.printing.document };

    if (menuName === 'dashboard')
      return { path: PATHS.dashboard.root, icon: HomeFilledIcon, selectedIcon: HomeIcon };

    return { path: null, icon: null, selectedIcon: null };
  };

  const menuToNav = (m) => {
    var { path, icon, selectedIcon } = menuHelper(m.menuNameEnglish, m.menuChildren.length);
    return {
      key: m.menuNameEnglish.toLowerCase(),
      url: path,
      label: chooseLan(m.menuNameLocal, m.menuNameEnglish),
      icon: icon,
      selectedIcon: selectedIcon,
    };
  };
  const menus = useMemo(() => {
    const findMenu = (name) => searchResults.find((m) => m.menuNameEnglish.toLowerCase() === name);
    const findChildIndex = (menu, name) =>
      menu?.menuChildren.findIndex((child) => child.menuNameEnglish.toLowerCase() === name) ?? -1;

    const moveChild = (fromName, toName, childName) => {
      const from = findMenu(fromName);
      const to = findMenu(toName);
      const index = findChildIndex(from, childName);

      if (index === -1 || !to) {
        return;
      }

      const [child] = from.menuChildren.splice(index, 1);

      if (findChildIndex(to, childName) === -1) {
        to.menuChildren.push(child);
      }
    };

    if (!findMenu('printing')) {
      searchResults.push({
        menuNameEnglish: 'Printing',
        menuNameLocal: i18n.t('printing:printing'),
        menuChildren: [
          {
            menuNameEnglish: 'Label printing',
            menuNameLocal: i18n.t('printing:label_printing'),
            menuChildren: [],
          },
          {
            menuNameEnglish: 'Document printing',
            menuNameLocal: i18n.t('printing:document_printing'),
            menuChildren: [],
          },
        ],
      });
    }

    moveChild('purchasing', 'inventory', 'purchase orders');
    moveChild('purchasing', SETTINGS_MENU_NAME, 'suppliers');

    return searchResults.filter(
      (m) => m.menuNameEnglish.toLowerCase() !== 'purchasing' || m.menuChildren.length > 0,
    );
  }, [searchResults, i18n]);

  const navItems = useMemo(() => {
    return menus
      .filter((m) => m.menuNameEnglish.toLowerCase() !== SETTINGS_MENU_NAME)
      .map((m) => {
        var nav = menuToNav(m);
        if (m.menuChildren.length > 0) {
          if (m.menuNameEnglish.toLowerCase() === 'outbound') {
            const pickingIndex = m.menuChildren.findIndex(
              (child) => child.menuNameEnglish.toLowerCase() === 'picking',
            );

            if (pickingIndex !== -1) {
              const hasSorting = m.menuChildren.some(
                (child) => child.menuNameEnglish.toLowerCase() === 'sorting',
              );

              if (!hasSorting) {
                const sortingMenu = {
                  menuNameEnglish: 'Sorting',
                  menuNameLocal: i18n.t('sorting:sorting'),
                  menuChildren: [],
                };
                m.menuChildren.splice(pickingIndex + 1, 0, sortingMenu);
              }
            }
          }

          nav.children = m.menuChildren.map((c) => {
            return menuToNav(c);
          });
        }
        return nav;
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathName, i18n, menus]);

  const settingsNavItems = useMemo(() => {
    const settingsToNav = (m) => {
      const key = toSettingsKey(m.menuNameEnglish);
      const children = m.menuChildren?.length ? m.menuChildren.map(settingsToNav) : undefined;

      return {
        key: key,
        label: chooseLan(m.menuNameLocal, m.menuNameEnglish),
        icon: SETTINGS_ICONS[key]?.icon,
        selectedIcon: SETTINGS_ICONS[key]?.selectedIcon,
        url: children ? undefined : SETTINGS_ROUTES[key],
        children: children,
      };
    };

    return settingMenus.map(settingsToNav);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [i18n, settingMenus]);

  const isSettingsMode = pathName.startsWith(PATHS.dashboard.settings.root);
  const items = isSettingsMode ? settingsNavItems : navItems;
  const hasSettingsMenu = isSettingAccess;

  const bottomItem = isSettingsMode
    ? {
        key: 'exit_settings',
        label: t('common:button.exit_settings'),
        icon: ExitIcon,
        selectedIcon: ExitIcon,
        onClick: () => router.push(PATHS.dashboard.root),
      }
    : {
        key: 'settings',
        label: t('common:button.settings'),
        icon: SettingsFilledIcon,
        selectedIcon: SettingsIcon,
        onClick: () => router.push(PATHS.dashboard.settings.root),
      };

  const handleMiniClick = (item) => {
    if (item.children?.length) {
      setNavOpen(true);
      return;
    }
    if (item.onClick) {
      item.onClick();
      return;
    }
    if (item.url) {
      router.push(item.url);
    }
  };

  const renderItems = (data) =>
    navOpen ? (
      <NavSectionVertical data={data} />
    ) : (
      <NavSectionMini
        data={data}
        onItemClick={handleMiniClick}
      />
    );

  return (
    <Box
      sx={{
        width: navOpen ? NAV_WIDTH_OPEN : NAV_WIDTH_CLOSED,
        height: '100%',
      }}
      className="dashboard_layout"
    >
      <Navigation location="/">
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: navOpen ? 'space-between' : 'flex-start',
              minHeight: '2.5rem',
              px: 'calc(12px + var(--p-space-200))',
              color: 'text.brand',
              '& .Polaris-Icon svg': { fill: 'currentColor' },
            }}
          >
            {navOpen && (
              <Typography
                variant="heading_sm"
                color="text.primary"
                noWrap
              >
                {isSettingsMode ? t('common:button.settings') : APP_SHORT_NAME}
              </Typography>
            )}
            <IconButton
              size="small"
              sx={{ p: 0 }}
              onClick={() => setNavOpen((prev) => !prev)}
            >
              <Box sx={{ width: '20px' }}>
                <Icon source={navOpen ? ChevronLeftIcon : ChevronRightIcon} />
              </Box>
            </IconButton>
          </Box>

          {searchLoading ? (
            <Loading />
          ) : (
            <>
              <Box sx={{ flexGrow: 1, overflowY: 'auto' }}>{renderItems(items)}</Box>

              {(hasSettingsMenu || isSettingsMode) && (
                <Box sx={{ pb: 'var(--p-space-300)' }}>{renderItems([bottomItem])}</Box>
              )}
            </>
          )}
        </Box>
      </Navigation>
    </Box>
  );
};

export default Navbar;
